import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';

import MailReplyComposer from '../chat/MailReplyComposer';
import { createDraft, sendDraft } from '../../services/mailIntelApi';
import { Btn } from './shared';

// A value the user has not decided yet: [in brackets, with a question?].
export const UNFILLED = /\[[^\]]*\?[^\]]*\]/;
// Same limit as the backend: one file in one request to Outlook.
export const MAX_FILE_BYTES = 3 * 1024 * 1024;

const splitAddresses = (text) =>
  (text || '')
    .split(/[,;\n]+/)
    .map((part) => part.trim())
    .filter(Boolean);

const sameList = (a, b) =>
  a.length === b.length &&
  a.every((value, i) => value.toLowerCase() === b[i].toLowerCase());

const escapeHtml = (text) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** The drafted text as the editor takes it: one paragraph per block. */
export const textToHtml = (text) =>
  (text || '')
    .trim()
    .split(/\n{2,}/)
    .filter((block) => block.trim())
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
    .join('');

/** What a reader sees of the editor's content. Parsed inert: nothing runs. */
export const htmlToText = (html) =>
  new DOMParser().parseFromString(html || '', 'text/html').body.textContent ||
  '';

const fileAsAttachment = (file) =>
  new Promise((resolve, reject) => {
    if (file.size > MAX_FILE_BYTES) {
      reject(new Error(`${file.name} is larger than 3 MB`));
      return;
    }
    const reader = new FileReader();
    reader.onload = () =>
      resolve({
        name: file.name,
        content_type: file.type || 'application/octet-stream',
        // "data:<type>;base64,<content>"
        content_base64: String(reader.result).split(',')[1] || '',
      });
    reader.onerror = () => reject(new Error(`${file.name} could not be read`));
    reader.readAsDataURL(file);
  });

/**
 * The reply as it will go out, in a window over the page, in the same editor
 * as every other mail reply. "Reply" on a draft opens it with the draft
 * filled in; nothing leaves until the user presses Send here.
 *
 * To and Cc are sent only if the user changed them; otherwise Outlook builds
 * them as for any reply-all.
 */
const ReplyForm = ({
  accessToken,
  threadId,
  targetId,
  draftId,
  variant,
  initial,
  canSend,
  onSent,
  onCancel,
}) => {
  const initialTo = splitAddresses(initial.to);
  const initialCc = splitAddresses(initial.cc);
  // A draft written from the prompt in this window replaces the one opened.
  const [current, setCurrent] = useState({ draftId, variant });
  const [messageBody, setMessageBody] = useState(() =>
    textToHtml(initial.body),
  );
  const [localAttachments, setLocalAttachments] = useState([]);
  const [driveAttachments, setDriveAttachments] = useState([]);
  const [customPrompt, setCustomPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  // Recipients changed since the draft was written: shown, then confirmed.
  const [recheck, setRecheck] = useState(null);
  // What the user last pressed Send with, to send again once confirmed.
  const lastPeople = useRef(null);

  // Escape closes the window, as the X does.
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !busy && onCancel();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const unfilled = UNFILLED.test(htmlToText(messageBody));

  const deliver = async ({ recipients, ccs, bccs }, acknowledged) => {
    const fields = {
      variant: current.variant,
      body_html: messageBody,
      acknowledged,
    };
    if (!sameList(recipients, initialTo)) fields.to = recipients;
    if (!sameList(ccs, initialCc)) fields.cc = ccs;
    if (bccs.length) fields.bcc = bccs;
    if (localAttachments.length)
      fields.attachments = await Promise.all(
        localAttachments.map(fileAsAttachment),
      );
    if (driveAttachments.length)
      fields.drive_file_ids = driveAttachments.map((file) => file.id);
    return sendDraft(accessToken, current.draftId, fields);
  };

  // false: nothing was sent and the editor stays as it is.
  const send = async (people) => {
    if (!htmlToText(messageBody).trim()) {
      throw new Error('Write the reply before sending.');
    }
    lastPeople.current = people;
    setBusy(true);
    try {
      const res = await deliver(people, false);
      setRecheck(null);
      onSent(res?.data);
      return true;
    } catch (error) {
      if (error?.code !== 'RECIPIENTS_CHANGED') throw error;
      setRecheck(error.details || { warnings: [], joined: [] });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const sendChecked = async () => {
    setBusy(true);
    try {
      const res = await deliver(lastPeople.current, true);
      setRecheck(null);
      toast.success('Reply sent successfully!');
      onSent(res?.data);
    } catch (error) {
      toast.error(error?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const generate = async (prompt) => {
    const res = await createDraft(accessToken, threadId, targetId, prompt);
    const first = res?.data?.variants?.[0];
    if (!first) return messageBody;
    setCurrent({ draftId: res.data.id, variant: 0 });
    setRecheck(null);
    return textToHtml(first.body);
  };

  const notice = (unfilled || !canSend || recheck) && (
    <div className="flex flex-col gap-2">
      {unfilled && (
        <p className="text-[12px] text-amber-300">
          Fill in or remove the values in [brackets] before sending.
        </p>
      )}
      {!canSend && (
        <p className="text-[12px] text-zinc-400">
          Reconnect Outlook in Integrations to send from here. You can still
          copy the text or save it to your Outlook drafts.
        </p>
      )}
      {recheck && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 text-[12px] text-amber-100">
          <p className="mb-1 font-semibold text-amber-300">
            Check the recipients before sending
          </p>
          {recheck.joined?.length > 0 && (
            <p>
              Added since this reply was written: {recheck.joined.join(', ')}
            </p>
          )}
          <ul className="list-disc pl-4">
            {(recheck.warnings || []).map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
          <div className="mt-2">
            <Btn
              variant="danger"
              onClick={sendChecked}
              disabled={busy || unfilled || !canSend}
            >
              I have checked, send anyway
            </Btn>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-100 flex items-center justify-center bg-black/75 p-3 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Reply"
        className="custom-scrollbar relative max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-zinc-700 bg-[#18181b] text-zinc-200 shadow-2xl"
      >
        <button
          type="button"
          aria-label="Close"
          title="Close"
          onClick={onCancel}
          disabled={busy}
          className="absolute right-3 top-3 z-10 rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100 disabled:opacity-40"
        >
          <X className="h-4 w-4" />
        </button>
        <MailReplyComposer
          onClose={onCancel}
          initialRecipients={initialTo}
          initialCcs={initialCc}
          initialBody={textToHtml(initial.body)}
          messageBody={messageBody}
          setMessageBody={setMessageBody}
          localAttachments={localAttachments}
          driveAttachments={driveAttachments}
          setLocalAttachments={setLocalAttachments}
          setDriveAttachments={setDriveAttachments}
          customPrompt={customPrompt}
          setCustomPrompt={setCustomPrompt}
          onSend={send}
          onGenerate={generate}
          sendBlocked={!canSend || unfilled || busy}
          notice={notice || null}
        />
      </div>
    </div>,
    document.body,
  );
};

export default ReplyForm;
